import React, { useContext, useEffect, useMemo } from 'react';

import { dashboardInvoiceApis } from '@apis/next/invoiceApis';
import { dashboardPaymentSlipApis } from '@apis/next/paymentSlipApis';
import { BookingDetailsContext } from '@components/admin/booking/helpers';
import { Button } from '@components/Button';
import {
  ActionableDialog,
  ActionableDialogTitle,
  ActionableDialogContent,
  ActionableDialogActions,
} from '@components/Dialog';
import { TextInput, TextInputOption } from '@components/NextForms/TextInput';
import { bookingInquiryStrings } from '@data/bookingInquiry';
import { invoiceDetailsMetaList, invoiceStrings } from '@data/invoice';
import { ledgerDivisions, ledgerStrings } from '@data/ledger';
import { useConfirmDialog } from '@hooks/useActionableDialog';
import { useDialogKey } from '@hooks/useDialogKey';
import { useFormUtils } from '@hooks/useFormUtils';
import { countryStrings } from '@models/Country';
import { InvoiceDetails, InvoiceDetailsForm } from '@models/next/Invoice';
import { InvoiceHelper } from '@models/next/InvoiceHelper';
import {
  MobxZodField,
  empty,
  getDecodeResult,
  getDecodeResultOr,
  unwrapDecodeResult,
} from '@monoid-dev/mobx-zod-form';
import { MobxZodObjectField } from '@monoid-dev/mobx-zod-form';
import { useForm } from '@monoid-dev/mobx-zod-form-react';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { useQueryClient } from '@tanstack/react-query';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { formatPrice } from '@utils/types';
import { observer } from 'mobx-react';
import { ZodTypeAny } from 'zod';

const strings = createStrings({
  ja: {
    addTitle: '請求名目を追加する',
    editTitle: '請求名目を編集する',
    d2dNo: 'D2D番号',
    tradeCondition: '貿易条件',
    invoiceName: '請求名目',
    invoiceNamePreparation: '名目備考',
    exchangeRate: '為替レート',
    exchangeRateDate: '為替レート日付',
    invoiceUnit: '請求単価',
    unitNumType: '数量・単位',
    unit: '単位',
    num: '数量',
    taxExcludedPrice: '非課税額',
    exciseTax: '消費税',
    unitPriceCurrency: '通貨',
    payerMark: '請求先マーク',
    confirmDelete: '本当に削除しますか？',
    title: {
      add: `${invoiceStrings.invoiceDetails}を追加する`,
      edit: `${invoiceStrings.invoiceDetails}を編集する`,
    },
  },
});

const placeholderStrings = createStrings({
  ja: {
    invoiceName: '請求名目を選択してください',
    invoiceNamePreparation: '備考を入力してください',
    exchangeRate: '為替レート',
    invoiceUnit: '単価',
    unitNumTypeUS: 'USD',
    unitNumTypeJP: 'JP',
    amount: '数量',
    unit: '単位',
    taxExcludedPrice: '非課税額を入力してください',
    exciseTax: '消費税',
    exciseTaxType: '課税区分',
  },
});

export interface InvoiceDetailsFormDialogProps {
  open: boolean;
  onClose: () => void;
  item?: InvoiceDetails;
}

const useStyles = makeMuiStyles((theme, common) => ({
  addBox: {
    display: 'grid',
    columnGap: '1rem',
    gridTemplateColumns: '3fr 1fr',
    alignItems: 'start',
  },
  columnBox: {
    ...common.flexColumn,
    ...common.verticalSpace1,
    flex: 1,
    minWidth: 0,
    padding: 10,
  },
  invoiceDetailsForm: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    alignItems: 'start',
    columnGap: 20,
  },
  invoiceDetailsFormColumn: {
    ...common.middleText,
    ...common.textGray700,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    rowGap: '1rem',
    columnGap: '1rem',
    padding: 10,
  },
  invoiceDetailsLabel: {
    paddingTop: 5,
  },
  invoiceDetailsData: {
    paddingTop: 5,
    height: 32,
  },
  flex1: {
    flex: 1,
  },
}));

const ComputedPriceDisplay: React.FC<{
  root: MobxZodObjectField<typeof InvoiceDetailsForm>;
  type: 'tax_excluded_price' | 'excise_tax';
}> = observer(({ root, type }) => {
  const { fields } = root;

  const currency = unwrapDecodeResult(fields.currency.decodeResult);
  const unit_price = getDecodeResultOr(fields.unit_price.decodeResult, 0);
  const unit_num = getDecodeResultOr(fields.unit_num.decodeResult, 0);
  const excise_tax_type = getDecodeResultOr(fields.excise_tax_type.decodeResult, '非課税');

  const { taxExcludedPriceForCurrency, exciseTaxForCurrency } =
    InvoiceHelper.getInvoiceDetailsComputed({
      currency,
      excise_tax_type,
      unit_num,
      unit_price,
    });

  if (type === 'excise_tax') {
    return formatPrice(exciseTaxForCurrency(0, currency) ?? 0, currency);
  }
  return formatPrice(taxExcludedPriceForCurrency(0, currency) ?? 0, currency);
});

const InvoiceDetailsFormContent: React.FC<InvoiceDetailsFormDialogProps> = ({ item, onClose }) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const type = item ? 'edit' : 'add';

  const { renderDialog, confirm } = useConfirmDialog();

  const initialOutput = useMemo(() => item && InvoiceHelper.mapInvoiceDetailsToForm(item), [item]);

  const form = useForm(InvoiceDetailsForm, {
    initialOutput: type === 'add' ? empty : initialOutput,
  });

  const { fields } = form.root;

  const { bookingInquiry } = useContext(BookingDetailsContext);

  const queryClient = useQueryClient();

  const invalidate = () => {
    queryClient.invalidateQueries(
      dashboardInvoiceApis.getKeyByPath('get', '/dashboard/booking-inquiry/:d2d_no/invoice-detail'),
    );
    queryClient.invalidateQueries(
      dashboardPaymentSlipApis.getKeyByPath('get', '/dashboard/payment-slip'),
    );
  };

  const postInvoiceDetails = dashboardInvoiceApis.usePost(
    '/dashboard/booking-inquiry/:d2d_no/invoice-detail',
    {
      params: {
        d2d_no: bookingInquiry.d2d_no,
      },
    },
    {
      onSuccess: invalidate,
    },
  );

  const patchInvoiceDetails = dashboardInvoiceApis.usePatch(
    '/dashboard/invoice-detail/:id',
    {
      params: {
        id: item?.id!,
      },
    },
    {
      onSuccess: invalidate,
    },
  );

  const deleteInvoiceDetails = dashboardInvoiceApis.useDelete(
    '/dashboard/invoice-detail/:id',
    {
      params: {
        id: item?.id!,
      },
    },
    {
      onSuccess: invalidate,
    },
  );

  const { renderAlertsAndSpinnerModal } = useFormUtils({
    mutations: [postInvoiceDetails, patchInvoiceDetails, deleteInvoiceDetails],
  });

  const renderLabel = (field: MobxZodField<ZodTypeAny>) => {
    return <label {...form.bindLabel(field)} className={classes.invoiceDetailsLabel} />;
  };

  const renderData = (data: React.ReactNode) => {
    return (
      <div style={{ gridColumn: 'span 3' }} className={classes.invoiceDetailsData}>
        {data}
      </div>
    );
  };

  const invoiceNameOptions: TextInputOption[] = useMemo(() => {
    return invoiceDetailsMetaList.map((item) => ({ value: item.name, label: item.name }));
  }, []);

  const debitOptions: TextInputOption[] = useMemo(() => {
    return Object.values(ledgerDivisions)
      .filter((v) => v.type === 'invoice')
      .map((v) => ({ label: v.debit, value: v.debit }));
  }, []);

  const updateForInvoiceName = () => {
    const invoice_name = getDecodeResult(fields.invoice_name.decodeResult);
    if (!invoice_name) return;

    const meta = invoiceDetailsMetaList.find((m) => m.name === invoice_name);
    if (!meta) {
      return;
    }

    fields.unit_num_type.setOutput(meta.unit_num_type);
    fields.debit.setOutput(ledgerDivisions[meta.division].debit);
  };

  useEffect(() => {
    // Don't update for 'edit' because it will overwrite the value at the beginning.
    if (type === 'add') {
      updateForInvoiceName();
    }
  }, []);

  return (
    <form
      {...form.bindForm({
        async onSubmit(values) {
          const credit = Object.values(ledgerDivisions).find(
            (v) => values.debit === v.debit,
          )?.credit;

          if (!credit) {
            throw new Error('Unexpected unfound credit');
          }

          const { expense_cost } =
            invoiceDetailsMetaList.find((m) => m.name === values.invoice_name) || {};

          if (!expense_cost) {
            throw new Error('Unexpected unfound invoice details meta');
          }

          if (type === 'add') {
            await postInvoiceDetails.mutateAsync({
              ...values,
              credit,
              expense_cost,
            });
          } else {
            await patchInvoiceDetails.mutateAsync({
              ...values,
              credit,
              expense_cost,
            });
          }

          onClose();
        },
      })}
    >
      <ActionableDialogTitle>{strings.title[type]}</ActionableDialogTitle>
      <ActionableDialogContent>
        {renderAlertsAndSpinnerModal()}
        {renderDialog()}

        <div className={classes.invoiceDetailsForm}>
          <div className={classes.invoiceDetailsFormColumn}>
            <label className={classes.invoiceDetailsLabel}>{bookingInquiryStrings.d2dNo}</label>
            {renderData(bookingInquiry.d2d_no)}

            <label className={classes.invoiceDetailsLabel}>
              {bookingInquiryStrings.tradeCondition}
            </label>
            {renderData(bookingInquiry.trade_condition)}

            {renderLabel(fields.invoice_name)}
            <div style={{ gridColumn: 'span 3' }}>
              <TextInput
                field={fields.invoice_name}
                inputProps={{ placeholder: placeholderStrings.invoiceName }}
                inputOptions={{ type: 'choose', options: invoiceNameOptions }}
                onSelect={updateForInvoiceName}
              />
            </div>

            {renderLabel(fields.debit)}
            <div style={{ gridColumn: 'span 3' }}>
              <TextInput
                field={fields.debit}
                inputProps={{ placeholder: ledgerStrings.division }}
                inputOptions={{ type: 'choose', options: debitOptions }}
              />
            </div>

            {renderLabel(fields.invoice_name_preparation)}
            <div style={{ gridColumn: 'span 3' }}>
              <TextInput
                field={fields.invoice_name_preparation}
                inputProps={{ placeholder: placeholderStrings.invoiceNamePreparation }}
                rows={3}
                multiline
              />
            </div>
          </div>

          <div className={classes.invoiceDetailsFormColumn}>
            {renderLabel(fields.unit_price)}

            <div style={{ gridColumn: 'span 2' }}>
              <TextInput
                field={fields.unit_price}
                inputProps={{ placeholder: placeholderStrings.amount }}
              />
            </div>

            <div style={{ gridColumn: 'span 1' }}>
              <TextInput field={fields.currency} />
            </div>

            <label className={classes.invoiceDetailsLabel}>{strings.unitNumType}</label>

            <div style={{ gridColumn: 'span 2' }}>
              <TextInput
                field={fields.unit_num}
                inputProps={{ placeholder: placeholderStrings.amount }}
              />
            </div>

            <div style={{ gridColumn: 'span 1' }}>
              <TextInput field={fields.unit_num_type} />
            </div>

            <label className={classes.invoiceDetailsLabel}>
              {invoiceStrings.tax_excluded_price}
            </label>
            {renderData(<ComputedPriceDisplay root={form.root} type="tax_excluded_price" />)}

            {renderLabel(fields.excise_tax_type)}
            <div style={{ gridColumn: 'span 2' }}>
              <TextInput field={fields.excise_tax_type} />
            </div>
            <div className={classes.invoiceDetailsData}>
              <ComputedPriceDisplay root={form.root} type="excise_tax" />
            </div>

            {renderLabel(fields.payer_mark)}
            <div style={{ gridColumn: 'span 3' }}>
              <TextInput
                field={fields.payer_mark}
                inputOptions={{
                  type: 'choose',
                  options: [
                    {
                      label: countryStrings.jp,
                      value: 'jp',
                    },
                    {
                      label: countryStrings.cn,
                      value: 'cn',
                    },
                  ],
                }}
              />
            </div>
          </div>
        </div>
      </ActionableDialogContent>
      <ActionableDialogActions justifyContent="left">
        {type === 'edit' && (
          <Button
            onClick={async () => {
              if (
                'confirmed' !==
                (await confirm({
                  message: strings.confirmDelete,
                }))
              ) {
                return;
              }

              await deleteInvoiceDetails.mutateAsync(undefined);
              onClose();
            }}
            color="grey"
            size="small"
            text={formStrings.delete}
            type="button"
            shape="round"
          />
        )}

        <Button
          color="primary"
          size="small"
          text={type === 'add' ? formStrings.create : formStrings.update}
          type="submit"
          shape="round"
        />
      </ActionableDialogActions>
    </form>
  );
};

export const InvoiceDetailsFormDialog: React.FC<InvoiceDetailsFormDialogProps> = (props) => {
  const { open, onClose } = props;

  const key = useDialogKey(open);

  return (
    <ActionableDialog key={key} open={open} onClose={onClose} size="lg">
      <InvoiceDetailsFormContent {...props} />
    </ActionableDialog>
  );
};
